<template>
  <div class="report-container-wrapper">
    <div>
      <div class="report-container">
        <div class="download-button-container">
          <v-spacer></v-spacer>
          <cta-button class="ml-4" @click="downloadPDF" :disabled="downloading">
            <v-icon left>mdi-download</v-icon>
            Download
          </cta-button>
        </div>
        <div
          class="report-content blue-border align-certificate-border pb-sm-8 pb-lg-16 px-sm-2 px-md-8 mt-4"
        >
          <v-icon class="mt-n8">$ribbon</v-icon>
          <v-spacer class="py-8" />

          <img
            class="center mt-n10 mb-5"
            src="@/assets/vendorproof-shadowmark@2x.png"
            alt="VendorProof Logo"
            width="75px"
            height="75px"
          />
          <h1 class="text-center mb-6">
            Certificate of Enrollment
          </h1>
          <p class="text-center">issued to</p>
          <h2 class="text-center">{{ vendor.name }}</h2>
          <div class="text-center body-1">
            <address class="d-inline-block" v-if="vendor.address">
              <div>
                {{ vendor.address.addrLine1 }}
              </div>
              <div>
                {{ vendor.address.addrLine2 }}
              </div>
              <div>
                {{ vendor.address.city }}, {{ vendor.address.state }}
                {{ vendor.address.zip }}
              </div>
            </address>
          </div>

          <div class="text-center">
            Enrollment Period:
            {{ customer.enrollmentCompleteDate | formatDate }} - present
          </div>

          <div class="py-10 mt-6 blue-border">
            <p class="text-center px-6 body-1 main-text">
              This certifies that the above listed company has completed
              VendorProof Engage enrollment for
              <strong>{{ customer.customerName }}</strong
              >. VendorProof Engage is a compliance platform that ensures
              healthcare organizations meet certain federal and state regulatory
              requirements including exclusion screening of the vendor company
              and its owners, and other compliance attestations. A completed
              enrollment in VendorProof engage includes supplying complete and
              accurate business information, and all required compliance
              documentation and attestations required by
              <strong>{{ customer.customerName }}</strong
              >.
            </p>
            <h3 class="text-center">Issued by: ProviderTrust, Inc.</h3>
            <div class="text-center">
              Issued on {{ new Date() | formatDate }}
            </div>
          </div>

          <div class="text-center mt-4 inquiries-text">
            Inquiries regarding VendorProof Preferred/Compliant Vendors can be
            directed to support@providertrust.com
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  customerDetailsAPI,
  customerDetailsWithVendorAPI,
  vendorInfoAPI,
  vendorInfoByIdAPI
} from "@/services/api";
import { get } from "@/services/request";
import { formatDate } from "@/services/format";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import BlueCallToAction from "@/components/providertrust/buttons/BlueCallToAction";
import hasNotifications from "@/mixins/has-notifications";

const reportName = "Proof of Enrollment";

export default {
  name: "ProofofEnrollment",
  props: {
    customerId: {
      type: String,
      required: true
    },
    vendorId: {
      type: String,
      required: false
    }
  },
  data: () => ({
    customer: {},
    vendor: {},
    reportName,
    downloading: false,
    showErrorMessage: false
  }),

  created() {
    let showErrorMessage = () =>
      this.showMessage({
        mode: "error",
        text: "User does not have access to the required information",
        timeout: 0
      });
    if (this.vendorId) {
      //viewing page as client
      get(vendorInfoByIdAPI.getUrl(this.vendorId))
        .then(response => {
          if (response && response.data) this.vendor = response.data;
        })
        .catch(showErrorMessage);
      get(
        customerDetailsWithVendorAPI.getUrl({
          customerId: this.customerId,
          vendorId: this.vendorId
        })
      )
        .then(response => {
          if (response && response.data) this.customer = response.data;
        })
        .catch(showErrorMessage);
    } else {
      //viewing page as vendor
      get(vendorInfoAPI)
        .then(response => {
          if (response && response.data) this.vendor = response.data;
        })
        .catch(showErrorMessage);
      get(customerDetailsAPI.getUrl({ customerId: this.customerId }))
        .then(response => {
          if (response && response.data) this.customer = response.data;
        })
        .catch(showErrorMessage);
    }
  },
  components: {
    "cta-button": BlueCallToAction
  },
  mixins: [hasNotifications],
  filters: {
    formatDate
  },
  methods: {
    downloadPDF() {
      this.logEvent("Downloaded Report", {
        "Report Name": `${reportName} (${this.vendor.name || ""})`
      });

      const filename = `${reportName}.pdf`;
      const reportContainer = document.querySelector(".report-content");
      // hide scroll bar temporarily: https://github.com/niklasvh/html2canvas/issues/1438#issuecomment-366727801
      const overflowVal = document.querySelector("html").style.overflow;
      document.querySelector("html").style.overflow = "hidden";

      this.downloading = true;
      document
        .querySelector(".report-content")
        .classList.add("align-certificate-border");

      this.$nextTick(() => {
        html2canvas(reportContainer, {
          scrollX: 0,
          scrollY: -window.pageYOffset,
          // fix grey area issue: https://github.com/niklasvh/html2canvas/issues/1937#issuecomment-520059242
          scale: 2
        })
          .then(canvas => {
            document.querySelector("html").style.overflow = overflowVal;
            let pdf = new jsPDF({
              orientation: "landscape",
              // default unit seems to work fine
              // unit: "px",
              format: "a4",
              compress: true
            });
            const width = pdf.internal.pageSize.getWidth();
            const height = (canvas.height / canvas.width) * width;
            pdf.addImage(
              canvas.toDataURL("image/png"),
              "PNG",
              0,
              0,
              width,
              height
            );
            pdf.save(filename);
          })
          .catch(err => {
            console.log("Error creating PDF", err);
            this.showMessage({
              mode: "error",
              text:
                "Error creating PDF, please try again or contact us for support"
            });
          })
          .finally(() => {
            this.downloading = false;
            document.querySelector("html").style.overflow = overflowVal;
            document
              .querySelector(".report-content")
              .classList.remove("align-certificate-border");
          });
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.download-button-container
  display: flex
  justify-content: right

.align-certificate-border
  height: 79.9vh

.blue-border
  border: 3px solid $color-pt-royal

//Hide the curved part of the ribbon
.report-content
  overflow: hidden

address
  font-style: normal

.main-text, .inquiries-text
  font-style: italic

*
  color: $color-pt-black
</style>
